// MIDAS V3 — Citation chip (Phase 2)
//
// Ports `Cite` from the design handoff:
//   ToDo/MidasChat/Claude_Design_files/machineid/design_handoff_midas_v3/
//   chat-window.jsx (line 54) and palette-tokens.css `.cite` atom.
//
// Visual: monospace pill with accent-soft bg + accent-line border + accent
// text. The `.cite` class is shared infrastructure — declared in
// midas-v3-tokens.scss in Phase 1 and inherited here. We expose the same
// class so designers can style it cohesively across the v3 surface.
//
// Click behaviour: dispatches `openArtifactAtPage` (the existing v2 thunk).
// In Phase 2 the v2 artifact dock receives the open call (the v3 dock ships
// in Phase 3). If the bot didn't emit a matching `cross_reference` artifact
// the slice no-ops — matches v2 behaviour.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useCallback } from "react";

import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import { openArtifactAtPage } from "../../midasPageSlice";

export interface CiteV3Props {
  /** Page number on the bot-side document to open. Required. */
  page: number;
  /** Optional human-readable label (e.g. "Sheet 175"). Children fall back. */
  label?: string;
  /** Optional accessible tooltip text shown via `title`. */
  tooltip?: string;
  children?: React.ReactNode;
}

const CiteV3: React.FC<CiteV3Props> = ({ page, label, tooltip, children }) => {
  const dispatch = useAppDispatch();

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!Number.isFinite(page) || page <= 0) return;
      dispatch(openArtifactAtPage({ page }));
    },
    [dispatch, page]
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      title={tooltip}
      data-cite-page={page}
      // Keep the visual treatment from palette-tokens.css `.cite` — small mono
      // pill with accent-soft fill + accent border + accent text. We render as
      // <button> (not <span>) so it's keyboard-focusable and announced as
      // interactive by screen readers.
      style={{
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "baseline",
        fontFamily: "var(--font-mono)",
        fontSize: 12,
        lineHeight: 1.4,
        padding: "1px 5px",
        margin: "0 1px",
        background: "var(--accent-soft)",
        color: "var(--accent)",
        borderRadius: 4,
        border: "1px solid var(--accent-line)",
        cursor: "pointer",
        whiteSpace: "nowrap",
        fontWeight: 500,
        // Reset default button styling.
        appearance: "none",
        WebkitAppearance: "none",
        // Quiet hover state — bump background opacity slightly.
        transition: "background 0.12s ease",
      }}
      className="midas-v3-cite"
    >
      {children ?? label ?? `p.${page}`}
    </button>
  );
};

export default CiteV3;
